import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseControllerProps,
  useController,
} from 'react-hook-form';

import dayjs, { type Dayjs, toDayjs } from 'src/helpers/dayjs';

type Props<T extends FieldValues, C> = UseControllerProps<T> & {
  control: Control<T, C>;
  disabled?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  label: string;
  margin?: 'dense' | 'normal' | 'none';
  maxDate?: Dayjs;
  name: FieldPath<T>;
};

/**
 * Controlled Date Picker component for use with react-hook-form
 * Handles conversion between Date (form value) and Dayjs (picker value)
 */
const ControlledDatePicker = <T extends FieldValues, C = unknown>({
  control,
  disabled,
  fullWidth = true,
  helperText,
  label,
  margin = 'dense',
  maxDate,
  name,
}: Props<T, C>) => {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error, invalid },
  } = useController<T>({
    name,
    control,
  });

  const dayjsValue = toDayjs(value);

  const handleChange = (newValue: Dayjs | null) => {
    onChange((newValue ? newValue.toDate() : null) as PathValue<T, FieldPath<T>>);
  };

  return (
    <MobileDatePicker
      label={label}
      value={dayjsValue}
      onChange={handleChange}
      maxDate={maxDate || dayjs()}
      disabled={disabled}
      slotProps={{
        textField: {
          inputRef: ref,
          onBlur,
          fullWidth,
          margin,
          error: invalid,
          helperText: error?.message || helperText,
        },
      }}
    />
  );
};

export default ControlledDatePicker;
